/**
 * The photo gallery's behavior half: reads the offering, lays its photos out by
 * count and steps the viewer. Draws nothing.
 */
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { SitePhoto } from '@telify/shared';

import { useSiteEvent, useSiteService } from '../../api/site';
import { usePieceEmit, usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import {
  contract,
  type PhotoGalleryActions,
  type PhotoGalleryEvents,
  type PhotoGalleryState,
  type PhotoGalleryThumb,
} from './contract';

export type PhotoGalleryRuntime = PieceRuntime<typeof contract, PhotoGalleryState, PhotoGalleryActions>;

export interface PhotoGalleryInput extends PieceInput<PhotoGalleryEvents> {
  /** The service whose photos to show; defaults to the page's service. */
  readonly serviceId?: string;
  /** The event whose photos to show; defaults to the page's event. */
  readonly eventId?: string;
}

/** How many thumbnails sit under the main photo at most. */
const THUMBS = 3;

export function usePhotoGallery(input: PhotoGalleryInput = {}): PhotoGalleryRuntime {
  const options = usePieceOptions(contract, input.options);
  const emit = usePieceEmit(input.onEvent);
  const params = useParams<{ serviceId?: string; eventId?: string }>();
  const explicit = Boolean(input.serviceId || input.eventId);
  const serviceId = explicit ? input.serviceId : params.serviceId;
  const eventId = explicit ? input.eventId : params.eventId;
  const serviceQuery = useSiteService(serviceId);
  const eventQuery = useSiteEvent(serviceId ? undefined : eventId);
  const query = serviceId ? serviceQuery : eventQuery;
  const offering = query.data;

  const photos = useMemo<SitePhoto[]>(
    () => (offering ? [offering.photo, ...offering.gallery].filter((p): p is SitePhoto => Boolean(p)) : []),
    [offering],
  );
  const thumbs = useMemo<PhotoGalleryThumb[]>(() => {
    const extras = photos.slice(1);
    const hidden = Math.max(0, extras.length - THUMBS);
    return extras.slice(0, THUMBS).map((photo, i) => ({
      photo,
      index: i + 1,
      more: i === THUMBS - 1 ? hidden : 0,
    }));
  }, [photos]);

  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const shown = viewerIndex !== null && viewerIndex < photos.length ? viewerIndex : null;

  const actions = useMemo<PhotoGalleryActions>(
    () => ({
      open: (index) => {
        if (!Number.isInteger(index) || index < 0 || index >= photos.length) return;
        setViewerIndex(index);
        emit('opened', { index });
      },
      go: (step) =>
        setViewerIndex((i) => (i === null ? i : Math.max(0, Math.min(photos.length - 1, i + Math.sign(step))))),
      close: () => {
        if (shown === null) return;
        setViewerIndex(null);
        emit('closed', {});
      },
    }),
    [photos.length, shown, emit],
  );

  const status: PhotoGalleryState['status'] = query.isError ? 'error' : query.isPending ? 'loading' : 'ready';
  return {
    state: {
      status,
      photos,
      thumbs,
      viewerIndex: shown,
      hasPrev: shown !== null && shown > 0,
      hasNext: shown !== null && shown < photos.length - 1,
    },
    actions,
    options,
  };
}
